import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Users, 
  HardDrive, 
  Plus, 
  Minus, 
  Calendar, 
  Receipt, 
  CheckCircle,
  AlertCircle,
  Crown,
  ArrowRight,
  Download,
  RefreshCw
} from 'lucide-react';
import api from '../utils/api';


const SubscriptionSettings = ({ user }) => {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Add-on modal state
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [addonType, setAddonType] = useState(null);
  const [addonQuantity, setAddonQuantity] = useState(1);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  // Check for addon success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('addon_success')) {
      setSuccess('Add-on purchased successfully!');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      fetchSubscriptionData();
    } else if (params.get('addon_canceled')) {
      setError('Add-on purchase was canceled.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('white_label_success')) {
      setSuccess('White Label add-on activated! You now have access to custom branding features.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchSubscriptionData();
    } else if (params.get('white_label_canceled')) {
      setError('White Label purchase was canceled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [subResponse, invoicesResponse] = await Promise.all([
        api.get(`/billing/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get(`/billing/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (subResponse.data.success) {
        setSubscription(subResponse.data.subscription);
      }
      if (invoicesResponse.data.success) {
        setInvoices(invoicesResponse.data.invoices);
      }
    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseAddon = async () => {
    setActionLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/billing/checkout/addon`,
        {
          addon_type: addonType,
          quantity: addonQuantity,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setActionLoading(false);
      setShowAddonModal(false);
    }
  };

  const handleSwitchBillingCycle = async (newCycle) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/billing/cycle/switch`,
        { billing_cycle: newCycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(response.data.message);
        fetchSubscriptionData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to switch billing cycle');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCustomerPortal = async () => {
    setActionLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/billing/customer-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.url) {
        // Open Stripe Customer Portal in new tab
        window.open(response.data.url, '_blank');
      } else {
        setError('Failed to open billing portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to open billing portal. You may need an active subscription first.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAddonPrice = (type) => {
    if (!subscription?.pricing) return 0;
    const isAnnual = subscription.billing_cycle === 'annual';
    
    switch (type) {
      case 'seats':
        return isAnnual ? subscription.pricing.seat_price_each : subscription.pricing.seat_price_each;
      case 'storage_100gb':
        return isAnnual ? 15.60 : 20.00; // 22% off for annual
      case 'storage_1tb':
        return isAnnual ? 39.00 : 50.00;
      default:
        return 0;
    }
  };

  const getWhiteLabelPrice = () => {
    const isAnnual = subscription?.billing_cycle === 'annual';
    return isAnnual ? 926.64 : 99.00;
  };

  const handlePurchaseWhiteLabel = async () => {
    setActionLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post('/billing/checkout/white-label', {
        origin_url: window.location.origin,
        source: 'settings'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Failed to create white label checkout:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading subscription...</span>
      </div>
    );
  }

  // Alpha user view
  if (subscription?.is_alpha) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Crown className="w-8 h-8 text-yellow-500" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Alpha Account</h3>
              <p className="text-gray-600">Unlimited access forever</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-white/50 rounded-lg p-4 text-center">
              <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">∞</p>
              <p className="text-sm text-gray-600">Seats</p>
            </div>
            <div className="bg-white/50 rounded-lg p-4 text-center">
              <HardDrive className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">∞</p>
              <p className="text-sm text-gray-600">Storage</p>
            </div>
            <div className="bg-white/50 rounded-lg p-4 text-center">
              <CreditCard className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">$0</p>
              <p className="text-sm text-gray-600">Monthly</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No subscription view
  if (!subscription) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-6">
            You don&apos;t have an active subscription. Subscribe to unlock all features.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            View Plans <ArrowRight size={18} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Current Plan Overview */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <h3 className="text-xl font-bold">Professional Plan</h3>
              <p className="text-purple-100 capitalize">{subscription.billing_cycle} billing</p>
            </div>
            <div className="text-right text-white">
              <p className="text-3xl font-bold">
                {formatPrice(subscription.pricing?.monthly_equivalent || 0)}
              </p>
              <p className="text-purple-100">/month</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Seats */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Users size={18} />
                <span className="text-sm font-medium">Seats</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {subscription.current_seats} / {subscription.total_seats}
              </p>
              <p className="text-sm text-gray-500">
                {subscription.available_seats} available
              </p>
            </div>

            {/* Storage */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <HardDrive size={18} />
                <span className="text-sm font-medium">Storage</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {subscription.total_storage_gb} GB
              </p>
              <p className="text-sm text-gray-500">
                {subscription.included_storage_gb} GB included
              </p>
            </div>

            {/* Billing Cycle */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Calendar size={18} />
                <span className="text-sm font-medium">Billing</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 capitalize">
                {subscription.billing_cycle}
              </p>
              {subscription.billing_cycle === 'monthly' && (
                <button
                  onClick={() => handleSwitchBillingCycle('annual')}
                  disabled={actionLoading}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Save 22% with annual →
                </button>
              )}
            </div>

            {/* Status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <CheckCircle size={18} />
                <span className="text-sm font-medium">Status</span>
              </div>
              <p className={`text-2xl font-bold capitalize ${
                subscription.status === 'active' ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {subscription.status}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Add Seats */}
        <button
          onClick={() => {
            setAddonType('seats');
            setAddonQuantity(1);
            setShowAddonModal(true);
          }}
          className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Add Seats</p>
            <p className="text-sm text-gray-500">
              {formatPrice(getAddonPrice('seats'))}/{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
            </p>
          </div>
        </button>

        {/* Add 100GB Storage */}
        <button
          onClick={() => {
            setAddonType('storage_100gb');
            setAddonQuantity(1);
            setShowAddonModal(true);
          }}
          className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <HardDrive className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Add 100GB</p>
            <p className="text-sm text-gray-500">
              {formatPrice(getAddonPrice('storage_100gb'))}/{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
            </p>
          </div>
        </button>

        {/* Add 1TB Storage */}
        <button
          onClick={() => {
            setAddonType('storage_1tb');
            setAddonQuantity(1);
            setShowAddonModal(true);
          }}
          className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <HardDrive className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Add 1TB</p>
            <p className="text-sm text-gray-500">
              {formatPrice(getAddonPrice('storage_1tb'))}/{subscription.billing_cycle === 'annual' ? 'year' : 'month'} (Best value)
            </p>
          </div>
        </button>
      </div>

      {/* White Label Add-on */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Crown size={20} className="text-yellow-500" />
          <h3 className="font-semibold text-gray-900">White Label</h3>
          {subscription.white_label_enabled && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              Active
            </span>
          )}
        </div>
        
        {subscription.white_label_enabled ? (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">White Label is Active</p>
                <p className="text-sm text-gray-600">
                  Custom email domains and branding features are enabled.
                </p>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/settings?group=branding&section=white-label'}
              className="mt-4 w-full py-2 border border-yellow-300 text-yellow-700 font-medium rounded-lg hover:bg-yellow-50 transition-colors flex items-center justify-center gap-2"
            >
              <span>Configure White Label Settings</span>
              <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-4">
              Make Athena completely yours with custom branding and your own email domain.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <CheckCircle size={16} className="text-green-500" />
                <span>Custom email domain</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <CheckCircle size={16} className="text-green-500" />
                <span>Branded notifications</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <CheckCircle size={16} className="text-green-500" />
                <span>Remove Athena branding</span>
              </div>
            </div>
            <button
              onClick={handlePurchaseWhiteLabel}
              disabled={actionLoading}
              className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {actionLoading ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Crown size={18} />
                  <span>
                    Add White Label - {formatPrice(getWhiteLabelPrice())}/{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
                  </span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Billing Cycle Switch */}
      {subscription.billing_cycle === 'monthly' && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Save 22% with Annual Billing</h4>
              <p className="text-sm text-gray-600">
                Switch to annual billing and save {formatPrice(subscription.pricing?.annual_savings || 0)} per year
              </p>
            </div>
            <button
              onClick={() => handleSwitchBillingCycle('annual')}
              disabled={actionLoading}
              className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {actionLoading ? 'Switching...' : 'Switch to Annual'}
            </button>
          </div>
        </div>
      )}

      {/* Invoice History */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt size={20} className="text-gray-600" />
            <h3 className="font-semibold text-gray-900">Invoice History</h3>
          </div>
          <button
            onClick={fetchSubscriptionData}
            className="text-purple-600 hover:text-purple-700"
          >
            <RefreshCw size={18} />
          </button>
        </div>
        
        {invoices.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">
                    {invoice.type === 'subscription' ? 'Subscription' : 
                     invoice.type === 'seats' ? 'Additional Seats' :
                     invoice.type.includes('storage') ? 'Storage Add-on' : invoice.type}
                  </p>
                  <p className="text-sm text-gray-500">{formatDate(invoice.date)}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatPrice(invoice.amount)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    invoice.status === 'paid' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {invoice.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No invoices yet
          </div>
        )}
      </div>

      {/* Manage Billing Portal */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Manage Billing</h4>
            <p className="text-sm text-gray-600">
              Update payment method, view invoices, or cancel subscription
            </p>
          </div>
          <button
            onClick={handleOpenCustomerPortal}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <CreditCard size={18} />
            {actionLoading ? 'Opening...' : 'Billing Portal'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Add-on Modal */}
      {showAddonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {addonType === 'seats' ? 'Add Team Seats' :
               addonType === 'storage_100gb' ? 'Add 100GB Storage' :
               'Add 1TB Storage'}
            </h3>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setAddonQuantity(Math.max(1, addonQuantity - 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <Minus size={18} />
                </button>
                <span className="text-2xl font-bold w-12 text-center">{addonQuantity}</span>
                <button
                  onClick={() => setAddonQuantity(addonQuantity + 1)}
                  className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Price per unit</span>
                <span>{formatPrice(getAddonPrice(addonType))}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Quantity</span>
                <span>× {addonQuantity}</span>
              </div>
              <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>
                  {formatPrice(getAddonPrice(addonType) * addonQuantity)}
                  /{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddonModal(false)}
                className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePurchaseAddon}
                disabled={actionLoading}
                className="flex-1 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
